import React, { useState, useEffect, useMemo } from 'react';
import {
  ChevronDown, ArrowLeft,
  ChevronRight,
  Download,
  Clock,
  Users,
  Euro
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import { useSupabase } from '../context/SupabaseContext';
import { LedgerEntry, Student } from '../components/interfaces/All';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const LedgerMonthly: React.FC = () => {
  const navigate = useNavigate();
  const { state, fetchMyLedger, fetchLessons, fetchTeachers, fetchStudents } = useSupabase();
  const [selectedTeacherId, setSelectedTeacherId] = useState<string>();

  let ledger = state.ledger || [];
  let students = state.students || [];

  // Filter by role
  switch (state.userRole) {
    case 'student':
      ledger = ledger.filter(l => l.student_id === state.userProfile.id);
      students = students.filter(s => s.id === state.userProfile.id);
      break;
  }

  const teacherLedger = useMemo(() =>
    selectedTeacherId
      ? ledger.filter(item => item.teacher_id === selectedTeacherId)
      : ledger,
    [ledger, selectedTeacherId]
  );

  // Auto-select current teacher
  useEffect(() => {
    if (state.userRole === 'teacher' && state.userProfile.id) {
      setSelectedTeacherId(state.userProfile.id);
    }
  }, [state.userRole, state.userProfile.id]);

  useEffect(() => {
    fetchLessons();
    fetchTeachers();
    fetchStudents();
  }, []);

  useEffect(() => {
    if (selectedTeacherId) {
      fetchMyLedger(selectedTeacherId);
    }
  }, [selectedTeacherId]);

  // Group ledger by month
  const monthlyGroups = useMemo(() => {
    const groups: Record<string, LedgerEntry[]> = {};

    teacherLedger.forEach(item => {
      const d = new Date(item.created_at || item.date);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!groups[monthKey]) groups[monthKey] = [];
      groups[monthKey].push(item);
    });

    return Object.entries(groups)
      .sort(([a], [b]) => (a > b ? -1 : 1))
      .map(([key, items]) => ({ key, items }));
  }, [teacherLedger]);

  const [openMonths, setOpenMonths] = useState<Record<string, boolean>>({});

  const toggleMonth = (monthKey: string) => {
    setOpenMonths(prev => ({ ...prev, [monthKey]: !prev[monthKey] }));
  };

  // Chart data
  // Chart data - TOTAL monthly value (not just income)
  const chartData = useMemo(() => {
    const monthlyTotals: Record<string, number> = {};

    teacherLedger.forEach(item => {
      const d = new Date(item.created_at || item.date);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      // TOTAL = raw amount (positive income, negative expenses)
      monthlyTotals[monthKey] = (monthlyTotals[monthKey] || 0) + item.amount;
    });

    return Object.entries(monthlyTotals)
      .sort(([a], [b]) => (a > b ? -1 : 1)) // Newest first
      .map(([month, total]) => ({
        month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        total  // Raw total (positive/negative)
      }));
    }, [teacherLedger]);

  const chartConfig = {
    labels: chartData.map(d => d.month),
    datasets: [{
      label: 'Monthly Total (€)',
      data: chartData.map(d => d.total || 0),
      // ✅ SAFE callbacks
      borderColor: (ctx: any) => ctx.parsed?.y >= 0 ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)',
      backgroundColor: (ctx: any) => ctx.parsed?.y >= 0
        ? 'rgba(34, 197, 94, 0.1)'
        : 'rgba(239, 68, 68, 0.1)',
      pointBackgroundColor: (ctx: any) => ctx.parsed?.y >= 0 ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)',
      // ... rest unchanged
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    // ✅ Prevent re-render issues
    animation: false,
    plugins: {
      legend: { position: 'top' as const },
      title: {
        display: true,
        text: 'Monthly Net Total',
        font: { size: 16, weight: 'bold' }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value: any) => {
            const val = Number(value);
            return isNaN(val) ? '€0' : val >= 0 ? `+€${val.toFixed(0)}` : `€${val.toFixed(0)}`;
          }
        }
      }
    }
  };

  const formatMonthLabel = (monthKey: string) => {
    const [year, month] = monthKey.split('-').map(Number);
    const d = new Date(year, month - 1, 1);
    return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  };

  const exportMonthPDF = (monthKey: string, items: LedgerEntry[]) => {
    const doc = new jsPDF({
      orientation: 'l',  // Landscape
      unit: 'mm',
      format: 'a4'
    });

    // Header
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text(formatMonthLabel(monthKey), 14, 20);

    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.text(`${items.length} transactions`, 14, 28);
    doc.text(`Total: €${items.reduce((sum, i) => sum + i.amount, 0).toFixed(2)}`, 14, 36);

    // Table
    autoTable(doc, {
      startY: 45,
      head: [['Date', 'Student', 'Description', 'Amount (€)', 'Type']],
      body: items.map(item => {
        const student = students.find(s => s.id === item.student_id);
        return [
          item.date,
          student ? `${student.first_name} ${student.last_name}` : 'Unknown',
          item.description || '',
          item.amount.toFixed(2),
          item.amount > 0 ? 'Income' : 'Expense'
        ];
      }),
      theme: 'grid',
      headStyles: {
        fillColor: [34, 197, 94],
        textColor: 255,
        fontSize: 12,
        fontStyle: 'bold'
      },
      styles: {
        fontSize: 10,
        cellPadding: 4,
        overflow: 'linebreak'
      },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 50 },
        3: { halign: 'right' },
        4: { cellWidth: 25 }
      },
      margin: { top: 45, left: 14, right: 14 },
      // ✅ FIXED: Type assertion for page count
      didDrawPage: (data: any) => {
        // Get page count AFTER table renders
        const pageCount = (doc as any).internal.getNumberOfPages();
        doc.setFontSize(10);
        doc.text(`Page ${data.pageNumber} of ${pageCount}`, 270, 290, { align: 'right' });
      }
    });

    doc.save(`ledger-${monthKey}.pdf`);
  };



  return (
    <div className="max-w-6xl mx-auto pb-20">
      <div className="mb-8">

        <div className="text-center mb-8">

          <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
            <Euro className="w-8 h-8" />
            Overview
          </h1>
          <p className="text-text-light text-lg">
            Track your income, expenses, and transactions by month
          </p>
        </div>
      </div>

      {/* Teacher select (Admin only) */}
      {state.userRole === 'admin' && (
        <div className="card p-6 mb-8">
          <label className="text-sm font-medium uppercase tracking-wide text-text-light flex items-center gap-2 mb-3">
            <Users className="w-4 h-4" />
            Select Teacher
          </label>
          <select
            className="w-full max-w-md px-4 py-3 border border-border rounded-xl bg-background focus:ring-2 focus:ring-emerald-500"
            value={selectedTeacherId || ''}
            onChange={(e) => setSelectedTeacherId(e.target.value || undefined)}
          >
            <option value="">All teachers</option>
            {state.teachers.map(t => (
              <option key={t.id} value={t.id}>
                {t.first_name} {t.last_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* 📈 INCOME CHART */}
      {teacherLedger.length > 0 && (
        <div className="card p-6 mb-8">
          <div className="h-96">
            <Line data={chartConfig} options={chartOptions} />
          </div>
        </div>
      )}

      {/* 📋 MONTHLY LEDGER */}
      <div>
        <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <Clock className="w-6 h-6" />
          Monthly Transactions
        </h2>

        {monthlyGroups.length === 0 ? (
          <div className="card p-12 text-center text-text-light">
            <Euro className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-xl">No transactions found</p>
            <p className="text-sm mt-2">Payments will appear here automatically</p>
          </div>
        ) : (
          <div className="space-y-4">
            {monthlyGroups.map(({ key, items }) => {
              const total = items.reduce((sum, i) => sum + i.amount, 0);
              const income = items.filter(i => i.amount > 0).reduce((s, i) => s + i.amount, 0);
              const expense = items.filter(i => i.amount < 0).reduce((s, i) => s + i.amount, 0);
              const isOpen = openMonths[key] ?? false;

              return (
                <div key={key} className="card" data-month={key} >
                  {/* Month header */}

                  <button
                      type="button"
                      className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 gap-4 hover:bg-gray-50/50"
                      onClick={() => toggleMonth(key)}
                    >
                    {/* Left: Month + Chevron */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {isOpen ? (
                        <ChevronDown className="w-5 h-5 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="w-5 h-5 flex-shrink-0" />
                      )}
                      <span className="text-xl font-bold">{formatMonthLabel(key)}</span>
                      <span className="text-sm text-text-light">({items.length} transactions)</span>
                    </div>

                    {/* Right: Stats - Stack on mobile, row on desktop */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 gap-2 w-full sm:w-auto">
                      <div className="flex items-center gap-4 justify-end text-sm">
                        <span className="text-emerald-600 font-semibold">+€{income.toFixed(2)}</span>
                        <span className="text-red-600 font-semibold">-€{Math.abs(expense).toFixed(2)}</span>
                        <span className={`font-bold px-3 py-1 rounded-full text-sm ${
                          total >= 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
                        }`}>
                          €{total.toFixed(2)}
                        </span>
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={e => {
                            e.stopPropagation();
                            exportMonthPDF(key, items);
                          }}
                          className="btn btn-outline flex items-center gap-1 px-3 py-1 text-sm hover:bg-emerald-50 cursor-pointer"
                          title="Export CSV"
                        >
                          <Download className="w-4 h-4" />

                        </div>

                      </div>


                    </div>
                  </button>

                  {/* Month transactions */}
                  {isOpen && (
                    <div className="border-t border-border p-6 space-y-3">
                      {items.map(item => {
                        const student = students.find(s => s.id === item.student_id);
                        return (
                          <div key={item.id} className="flex items-center justify-between p-2 bg-gray-50/50 rounded-xl">
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900">
                                {student ? `${student.first_name} ${student.last_name}` : 'Unknown Student'}
                              </div>
                              <div className="text-sm text-text-light">{item.date}</div>
                              <div className="text-sm text-text-light">{item.description}</div>
                            </div>
                            <div className={`font-bold text-xl ${
                              item.amount > 0 ? 'text-emerald-600' : 'text-red-600'
                            }`}>
                              {item.amount > 0 ? '+' : ''}€{Math.abs(item.amount).toFixed(2)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LedgerMonthly;
